import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shigo-ui/ui';
import { DashboardPageFormBody } from '@shigo-ui/dashboard-ui';

import { type MadBuyConversionPixel, type MadBuyLineItemGeneralForm } from '../../lineItemsModel';
import { dashboardT } from '../../../../../lib/messages';
import { FormField } from './LineItemEditorFields';
import type { LineItemFormUpdate } from './LineItemEditorTypes';

export function LineItemConversionTrackingFields({
  form,
  loading,
  pixels,
  updateField,
}: {
  form: MadBuyLineItemGeneralForm;
  loading: boolean;
  pixels: MadBuyConversionPixel[];
  updateField: LineItemFormUpdate;
}) {
  const selectedPixel = pixels.find((pixel) => pixel.id === form.conversionPixelId);

  return (
    <DashboardPageFormBody
      title={dashboardT('madBuy.lineItems.conversionTracking')}
      description={dashboardT('madBuy.lineItems.conversionTrackingDescription')}
    >
      <FormField label={dashboardT('madBuy.lineItems.conversionPixel')}>
        <Select
          value={form.conversionPixelId || 'none'}
          onValueChange={(value) => {
            updateField('conversionPixelId', value === 'none' ? '' : value);
          }}
        >
          <SelectTrigger aria-label={dashboardT('madBuy.lineItems.conversionPixel')}>
            <SelectValue
              placeholder={
                loading
                  ? dashboardT('madBuy.lineItems.loadingPixels')
                  : dashboardT('madBuy.common.none')
              }
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">{dashboardT('madBuy.common.none')}</SelectItem>
            {pixels.map((pixel) => (
              <SelectItem key={pixel.id} value={pixel.id}>
                {pixel.name} ({pixel.esid})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>

      {selectedPixel ? (
        <div className="border-border bg-muted/20 grid gap-3 rounded-md border p-4 text-sm">
          <PixelSummaryRow label={dashboardT('madBuy.common.esid')} value={selectedPixel.esid} />
          <PixelSummaryRow
            label={dashboardT('madBuy.common.type')}
            value={formatEnumLabel(selectedPixel.triggerType ?? selectedPixel.type)}
          />
          <PixelSummaryRow
            label={dashboardT('madBuy.lineItems.attributionWindow')}
            value={`${String(selectedPixel.attributionWindowValue ?? '-')} ${formatEnumLabel(
              selectedPixel.attributionWindowType,
            )}`}
          />
          <Button
            type="button"
            variant="outline"
            className="justify-self-start rounded-full"
            onClick={() => {
              updateField('conversionPixelId', '');
            }}
          >
            {dashboardT('madBuy.lineItems.removePixel')}
          </Button>
        </div>
      ) : null}
    </DashboardPageFormBody>
  );
}

function PixelSummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground font-medium">{label}</span>
      <span className="text-foreground text-right">{value}</span>
    </div>
  );
}

function formatEnumLabel(value: string | undefined) {
  if (!value) {
    return '-';
  }

  return value
    .toLowerCase()
    .split('_')
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}
